/**
 * **How a stick and a keyboard share one aeroplane.**
 *
 * They are not the same kind of control and cannot simply be added. A key is a
 * *request* that the stick move — which is why `packages/engine`'s
 * `advanceStick` exists, and why STICK AUTHENTIC ramps it over a second and a
 * half (AD-007n change 4). An analogue axis is not a request at all: it **is**
 * the position, measured off a lever the player's hand is already holding.
 * Ramping it would be shaping a hand's movement a second time, and both
 * `stick.ts` and `stick-settings.ts` said so before this file existed: *"an
 * analogue axis — gamepad, mouse-stick — gets its deflection immediately either
 * way."*
 *
 * So the rule is **last device to speak owns the axis**, and it owns it whole:
 *
 * - Touch a key and the keyboard has the stick; it ramps, at whatever shaping
 *   the player chose, exactly as it always did.
 * - Move the stick past its dead zone and the stick has it; the shaping is
 *   bypassed and the deflection is the axis reading, immediately.
 * - Neither, and whoever had it keeps it — which is what makes a stick released
 *   to centre read as *centred* at once (the pad still owns the axis and is
 *   reporting zero) while a key released after a tap still gets its authentic
 *   roll-out (the keyboard still owns it and `advanceStick` is still running).
 *
 * The two never blend, and that is deliberate: averaging a held key with a
 * deflected stick would produce a position neither hand asked for, and the
 * player would have no way to tell which control he was fighting.
 *
 * ## The handover is what makes an unplug safe
 *
 * Whichever source wins, its position is written back into the carried stick.
 * That costs nothing while the pad is driving and is the whole of the
 * hot-unplug story: a device that vanishes at full right aileron leaves the
 * carried stick at full right aileron, ownership falls back to the keyboard on
 * the very next frame, and `advanceStick` walks it home over the shaping's own
 * release time — the aeroplane rolls out of the turn the way it would if a hand
 * had let go, rather than snapping level or freezing at full deflection.
 *
 * Pure, and separate from `scene-inputs.ts` for that reason: every case above
 * is a table-driven unit test.
 */

import type { StickAxes } from "@red-opus/engine";
import type { PadAxes } from "./gamepad-axes.ts";
import type { PadAction } from "./gamepad-mapping.ts";
import type { GlanceDirection } from "./keys.ts";

/** Which device is currently holding an axis. */
export type InputSource = "keyboard" | "gamepad";

export interface StickBlend {
  readonly source: InputSource;
  readonly stick: StickAxes;
}

/** True when any of the three control axes is mapped *and* reading outside its dead zone. */
export function padStickDeflected(axes: PadAxes): boolean {
  return (axes.pitch ?? 0) !== 0 || (axes.roll ?? 0) !== 0 || (axes.yaw ?? 0) !== 0;
}

/** True when the player is asking for any of the three control axes from the keyboard this frame. */
export function keyStickActive(demand: StickAxes): boolean {
  return demand.pitch !== 0 || demand.roll !== 0 || demand.yaw !== 0;
}

/**
 * Who owns the stick this frame.
 *
 * The keyboard is tested **first**, so pressing a key takes the aeroplane back
 * even from a stick that is still deflected. That direction is the one that has
 * to be free: the keyboard is the fallback every player has, and a stick left
 * leaning against something must never be able to lock him out of it.
 *
 * `padPresent` is false when there is no device *or* when a full-screen panel
 * is swallowing it, and it hands ownership straight back rather than leaving it
 * with a device that is gone. The blend below would fall back to the keyboard's
 * position either way — every role reads `undefined` — but leaving the flag
 * saying "gamepad" while nothing is plugged in is a lie the next reader of this
 * code would have to work out for himself.
 */
export function resolveStickSource(
  previous: InputSource,
  keyActive: boolean,
  padDeflected: boolean,
  padPresent: boolean,
): InputSource {
  if (keyActive || !padPresent) return "keyboard";
  if (padDeflected) return "gamepad";
  return previous;
}

/**
 * The stick position to fly, given the keyboard's ramped one and whatever the
 * pad is reading. A role the pad has no axis for always falls back to the
 * keyboard's, so a two-axis stick can fly pitch and roll while `Z`/`X` still
 * work the rudder bar.
 */
export function blendStick(source: InputSource, keyStick: StickAxes, axes: PadAxes): StickAxes {
  if (source === "keyboard") return keyStick;
  return {
    pitch: axes.pitch ?? keyStick.pitch,
    roll: axes.roll ?? keyStick.roll,
    yaw: axes.yaw ?? keyStick.yaw,
  };
}

/**
 * The throttle does **not** get the last-device-to-speak rule, and the reason
 * is that it is the one control that is genuinely absolute.
 *
 * A stick springs back, so "who is holding it" is a real question with a
 * changing answer. A quadrant does not: the lever is where the player's hand
 * left it, and there is no reading it could give that means *"I am not being
 * used"*. So a mapped throttle axis simply **is** the throttle, and `+`/`-`
 * and the wheel are ignored while it is plugged in. Arbitrating between them
 * could only produce a lever that disagrees with the one on the desk.
 *
 * That costs the keyboard nothing in practice, because a throttle axis is
 * never mapped by default — the standard controller layout leaves it
 * deliberately unmapped (`gamepad-mapping.ts`), so it takes a player
 * calibrating a real quadrant to reach this branch at all, and that player has
 * a lever in his hand.
 *
 * Unplugging is handled by the same line: `padThrottle` becomes `undefined`,
 * `keyTravel` is `advanceThrottle` over the carried lever, and because the
 * carried lever was being written from the axis every frame it is already at
 * the setting the pad last held. The engine does not lurch, and the keys pick
 * up from there.
 */
export function resolveThrottle(keyTravel: number, padThrottle: number | undefined): number {
  return padThrottle ?? keyTravel;
}

/**
 * The shoulder buttons' contribution to the throttle lever, as a travel delta
 * for this frame.
 *
 * Expressed the same way the mouse wheel's is (`keys.ts`'s `wheelDelta`) so it
 * can be summed with it before `advanceThrottle`'s single clamp — and at
 * exactly the rate a held `+` key travels, so the two feel like one lever
 * rather than two. Holding both cancels, like a centred axis.
 */
export function padThrottleTravel(held: ReadonlySet<PadAction>, dt: number, travelSeconds: number): number {
  const direction = (held.has("throttleUp") ? 1 : 0) - (held.has("throttleDown") ? 1 : 0);
  if (direction === 0 || travelSeconds <= 0) return 0;
  return (direction * dt) / travelSeconds;
}

/**
 * Where the pilot is looking, from the keyboard's `Q`/`E`/`R`/`T`/`G` and the
 * stick's hat and back-glance button together.
 *
 * The hat is the natural home for the snap views — it is a thumb already on the
 * stick, and the original's own six-way cockpit snap (`F1`–`F6`, transcribed in
 * `keys.ts`) is exactly four hat positions plus forward and back. Back stays on
 * a *button* rather than a hat position, because the hat has four cardinals and
 * back is the fifth thing to look at, and because checking six is a reflex that
 * wants a whole finger rather than a thumb pushed to a diagonal.
 *
 * A held glance key out-votes the hat, on the same principle the axes use: the
 * keyboard can always take a control back.
 */
export function resolveGlance(
  keyGlance: GlanceDirection,
  padGlance: GlanceDirection | undefined,
  padLookBack: boolean,
): GlanceDirection {
  if (keyGlance !== "forward") return keyGlance;
  if (padLookBack) return "back";
  return padGlance ?? "forward";
}
